/**
 * Tipos e Constantes para Verticais de Processos (TAREFA-224)
 * 
 * Define os tipos de verticais/fluxos disponíveis no sistema e
 * o mapeamento de rotas para navegação correta.
 */

/**
 * Tipos de verticais/fluxos de processos
 */
export const TipoVerticalProcesso = {
  ANALISE_PROCESSUAL: 'analise_processual',
  CRIACAO_PETICAO: 'criacao_peticao',
  CONTESTACAO: 'contestacao',
  RECURSO: 'recurso',
} as const;

export type TipoVerticalProcesso = typeof TipoVerticalProcesso[keyof typeof TipoVerticalProcesso];

/**
 * Labels amigáveis para exibição no frontend
 */
export const LABELS_TIPO_VERTICAL: Record<string, string> = {
  [TipoVerticalProcesso.ANALISE_PROCESSUAL]: 'Análise Processual',
  [TipoVerticalProcesso.CRIACAO_PETICAO]: 'Criação de Petição',
  [TipoVerticalProcesso.CONTESTACAO]: 'Contestação',
  [TipoVerticalProcesso.RECURSO]: 'Recurso',
};

/**
 * Mapeamento de tipo de vertical para rota de continuação
 * Usado no Dashboard para redirecionar corretamente ao continuar um processo
 */
export const ROTAS_POR_TIPO_VERTICAL: Record<string, string> = {
  [TipoVerticalProcesso.ANALISE_PROCESSUAL]: '/analise-peticao',
  [TipoVerticalProcesso.CRIACAO_PETICAO]: '/criacao-peticao-inicial',
  // Futuras verticais:
  // [TipoVerticalProcesso.CONTESTACAO]: '/contestacao',
  // [TipoVerticalProcesso.RECURSO]: '/recurso',
};

/**
 * Cores para badges de tipos de verticais (Tailwind classes)
 */
export const CORES_TIPO_VERTICAL: Record<string, string> = {
  [TipoVerticalProcesso.ANALISE_PROCESSUAL]: 'bg-blue-100 text-blue-800',
  [TipoVerticalProcesso.CRIACAO_PETICAO]: 'bg-green-100 text-green-800',
  [TipoVerticalProcesso.CONTESTACAO]: 'bg-yellow-100 text-yellow-800',
  [TipoVerticalProcesso.RECURSO]: 'bg-purple-100 text-purple-800',
};

/**
 * Retorna o label amigável para um tipo de vertical
 * @param tipoVertical - Tipo de vertical do processo
 * @returns Label amigável ou tipo original se não encontrado
 */
export function obterLabelTipoVertical(tipoVertical?: string): string {
  if (!tipoVertical) return 'Não Especificado';
  return LABELS_TIPO_VERTICAL[tipoVertical] || tipoVertical;
}

/**
 * Retorna a rota de navegação para um tipo de vertical
 * @param tipoVertical - Tipo de vertical do processo
 * @returns Rota de navegação ou rota padrão
 */
export function obterRotaPorTipoVertical(tipoVertical?: string): string {
  if (!tipoVertical) return '/analise-peticao'; // Rota padrão (retrocompatibilidade)
  return ROTAS_POR_TIPO_VERTICAL[tipoVertical] || '/analise-peticao';
}

/**
 * Retorna as classes CSS para o badge de tipo de vertical
 * @param tipoVertical - Tipo de vertical do processo
 * @returns Classes Tailwind CSS
 */
export function obterCoresTipoVertical(tipoVertical?: string): string {
  if (!tipoVertical) return 'bg-gray-100 text-gray-800';
  return CORES_TIPO_VERTICAL[tipoVertical] || 'bg-gray-100 text-gray-800';
}
